import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      toast.error('Payment verification timed out. Please check your email.');
      return;
    }

    try {
      // Check payment status directly from Supabase
      const { data: payment, error } = await supabase
        .from('payment_transactions')
        .select('status, stripe_session_id')
        .eq('stripe_session_id', sessionId)
        .single();

      if (error) {
        // If not found in DB, payment might still be processing
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, pollInterval);
        return;
      }
      
      if (payment?.status === 'paid' || payment?.status === 'complete') {
        setStatus('success');
        toast.success('Payment successful!');
        return;
      } else if (payment?.status === 'expired' || payment?.status === 'failed') {
        setStatus('expired');
        toast.error('Payment session expired or failed');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(checkPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Status check error:', error);
      // On error, assume success if coming from Stripe redirect
      // (Stripe only redirects on successful payment)
      setStatus('success');
      toast.success('Payment processed!');
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] flex items-center justify-center" data-testid="checking-status">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#003B5C] animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Verifying Payment...
          </h2>
          <p className="text-slate-600 mt-2">Please wait while we confirm your purchase</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] flex items-center justify-center py-12 px-6" data-testid="success-page">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-12 max-w-2xl text-center shadow-xl"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-[#74A57F] to-[#2A9D8F] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Payment Successful!
          </h1>
          
          <p className="text-xl text-slate-600 mb-8">
            Thank you for your purchase. Your QLD Green-Ready ESG Tracker has been sent to your email.
          </p>
          
          <div className="bg-[#EDF6F9] rounded-xl p-6 mb-8">
            <Download className="w-8 h-8 text-[#003B5C] mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-[#003B5C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Check Your Email
            </h3>
            <p className="text-slate-600">
              Your download link and access instructions have been sent. Check your inbox (and spam folder) within the next few minutes.
            </p>
          </div>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Need help? Contact us at <a href="mailto:support@lucentsky.com.au" className="text-[#003B5C] font-medium hover:underline">support@lucentsky.com.au</a>
            </p>
            
            <Button
              onClick={() => navigate('/')}
              className="bg-[#003B5C] text-white hover:bg-[#002A42] rounded-full px-8 py-6 text-lg font-medium"
              data-testid="return-home-button"
            >
              Return to Home
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] flex items-center justify-center py-12 px-6" data-testid="error-page">
      <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-12 max-w-2xl text-center shadow-xl">
        <h2 className="text-3xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Something Went Wrong
        </h2>
        <p className="text-slate-600 mb-8">
          We encountered an issue verifying your payment. Please contact support if you've been charged.
        </p>
        <Button
          onClick={() => navigate('/')}
          className="bg-[#003B5C] text-white hover:bg-[#002A42] rounded-full px-8 py-6 text-lg font-medium"
        >
          Return to Home
        </Button>
      </div>
    </div>
  );
};

export default SuccessPage;
